/**
 * "Continue with Google" (docs/AUTH.md): Firebase Auth proves the Google identity in a popup; we take its ID token to
 * POST /auth/google and keep only our own session. Firebase is imported on tap, so it's never in the main bundle, and
 * the Firebase sign-in is ended right away (identity only).
 */
import { config } from "../env.ts";

export const googleConfigured = (f: { apiKey: string; authDomain: string; projectId: string } = config.firebase) =>
  !!(f.apiKey && f.authDomain && f.projectId);

/** Thrown when the person closed the popup: not an error to show. */
export class GoogleCancelled extends Error { constructor() { super("cancelled"); this.name = "GoogleCancelled"; } }

export async function googleIdToken(): Promise<string> {
  const [{ getApps, initializeApp }, fb] = await Promise.all([import("firebase/app"), import("firebase/auth")]);
  const { apiKey, authDomain, projectId } = config.firebase;
  const app = getApps()[0] ?? initializeApp({ apiKey, authDomain, projectId });
  const auth = fb.getAuth(app);
  const provider = new fb.GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" }); // so "use a different account" is always possible
  try {
    const cred = await fb.signInWithPopup(auth, provider);
    return await cred.user.getIdToken();
  } catch (e) {
    const code = (e as { code?: string }).code ?? "";
    if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request" || code === "auth/user-cancelled") throw new GoogleCancelled();
    if (code === "auth/popup-blocked") throw new Error("Your browser blocked the Google window. Allow pop-ups for this site, or use \"Email me a code\".");
    if (code === "auth/network-request-failed") throw new Error("We couldn't reach Google. Check the internet and try again.");
    if (code === "auth/operation-not-allowed" || code === "auth/configuration-not-found") throw new Error("Google sign-in isn't turned on for CleanCleaver yet. Use \"Email me a code\" for now.");
    if (code === "auth/unauthorized-domain") throw new Error("Google sign-in isn't set up for this address yet. Use \"Email me a code\" for now.");
    throw new Error("Google sign-in didn't work. Try again, or use \"Email me a code\".");
  } finally {
    void fb.signOut(auth).catch(() => {});
  }
}
